// TranslationContext.tsx
import React, { createContext, useContext, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationContextType } from './TranslationContextType';

// Crear el contexto
const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Hook para usar el contexto
export const useTranslationContext = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslationContext must be used within a TranslationProvider');
  }
  return context;
};

// Proveedor del contexto
export const TranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();

  // Función recursiva que recorre los children y traduce los nodos de texto
  const applyTranslation = (element: ReactNode): ReactNode => {
    // Si es un string, aplicamos la traducción
    if (typeof element === 'string') {
      return t(element);
    }

    // Si es un React element, clonamos sus hijos y aplicamos la traducción recursivamente
    if (React.isValidElement(element)) {
      return React.cloneElement(element, {
        ...element.props,
        children: React.Children.map(element.props.children, applyTranslation),
      });
    }

    // Si no es ni un string ni un elemento de React, lo devolvemos sin cambios
    return element;
  };

  return (
    <TranslationContext.Provider value={{ t, i18n }}>
      {applyTranslation(children)}
    </TranslationContext.Provider>
  );
};

